import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';
import { getHighlight, isHighlightSeen, HIGHLIGHTS_EVENT, markHighlightSeen } from 'src/data/product-highlights';

import { Box, Stack, Button, Popover, Typography } from '@mui/material';

import { useRouter } from 'src/routes/hooks';

import { track } from 'src/utils/analytics';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

// Rilegge la memoria quando una novità viene vista, anche da un'altra pagina o scheda.
function useHighlightsSeen(ids) {
  const key = ids.join('|');
  const read = useCallback(() => key.split('|').every((id) => isHighlightSeen(id)), [key]);
  const [seen, setSeen] = useState(read);

  useEffect(() => {
    const refresh = () => setSeen(read());
    window.addEventListener(HIGHLIGHTS_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(HIGHLIGHTS_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [read]);

  return seen;
}

const dotSx = (size) => ({
  width: size,
  height: size,
  ml: 1,
  flexShrink: 0,
  borderRadius: '50%',
  display: 'inline-block',
  verticalAlign: 'middle',
  bgcolor: 'secondary.main',
  boxShadow: (theme) => `0 0 0 0 ${theme.palette.secondary.main}`,
  animation: 'heyjob-highlight-pulse 2s ease-out infinite',
  '@keyframes heyjob-highlight-pulse': {
    '0%': { boxShadow: '0 0 0 0 rgba(250, 149, 24, 0.6)' },
    '70%': { boxShadow: '0 0 0 6px rgba(250, 149, 24, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(250, 149, 24, 0)' },
  },
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

// La voce del menu è un link: i clic sul puntino e nel fumetto non devono aprire la pagina.
function stop(event) {
  event.preventDefault();
  event.stopPropagation();
}

// Puntino della novità accanto a una voce del menu (spazio «info»), con il suo fumetto.
export function HighlightDot({ id }) {
  const router = useRouter();
  const highlight = getHighlight(id);
  const seen = useHighlightsSeen([id]);
  const [anchorEl, setAnchorEl] = useState(null);

  if (!highlight) {
    return null;
  }

  const open = (event) => {
    stop(event);
    setAnchorEl(event.currentTarget);
    track('highlight_opened', { highlight: id });
  };

  const close = (via) => {
    setAnchorEl(null);
    markHighlightSeen(id);
    track('highlight_dismissed', { highlight: id, via });
  };

  return (
    <>
      {!seen && (
        <Box
          component="span"
          role="button"
          tabIndex={0}
          aria-label={`Novità: ${highlight.title}`}
          onClick={open}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              open(event);
            }
          }}
          sx={{ ...dotSx(10), cursor: 'pointer' }}
        />
      )}
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => close('escape')}
        // Il fumetto è disegnato dentro il link della voce: senza questo, anche il clic
        // sullo sfondo per chiuderlo risalirebbe al link e aprirebbe la pagina.
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        marginThreshold={16}
        slotProps={{ paper: { onClick: (event) => event.stopPropagation(), sx: { p: 2, maxWidth: 300 } } }}
      >
        <Stack spacing={1.25}>
          <Label
            color="success"
            variant="soft"
            startIcon={<Iconify icon="solar:stars-bold-duotone" />}
            sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
          >
            Nuovo
          </Label>
          <Typography variant="subtitle1">{highlight.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {highlight.text}
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button color="inherit" onClick={() => close('ok')}>
              Ho capito
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                close('discover');
                router.push(highlight.path);
              }}
            >
              Scopri
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
}

HighlightDot.propTypes = { id: PropTypes.string.isRequired };

// Puntino passivo sulla voce madre: dentro il gruppo chiuso c'è una novità.
export function HighlightParentDot({ ids, floating = false }) {
  const seen = useHighlightsSeen(ids);
  return seen ? null : <Box component="span" aria-hidden sx={{ ...dotSx(8), ...(floating && { ml: 0 }) }} />;
}

HighlightParentDot.propTypes = { ids: PropTypes.arrayOf(PropTypes.string).isRequired, floating: PropTypes.bool };
